<template>
  <div class="d-f f-1 ai-c jc-c" v-if="state === 'loading'">
    <div class="loading-spinner large"></div>
  </div>
  <div v-else-if="state === 'loaded'">
    <card-minimal-title title="Statistics per day" />
    <card-minimal :subtitle="`Statistics`" subtitleIcon="line-graph-24" class="flex f-3 graph">
      <template slot="subtitle-slot">
        <FormDropdown :items="timeera" @input="timeera = $event.value" class="minimal inline blue m-l-2" />
      </template>
      <Graph ref="dayGraph" :data="{ labels: dayStats.labels, datasets: dayStats.datasets }" :options="dayStats.options" />
      <KeyValues>
        <template v-for="(dataset, key) in dayStats.graph">
          <Key v-if="dataset.show" :key="`${key}-${dayStats.timeEra}`" :color="dataset.colorName" :icon="dataset.icon" :active="dayStats.activeDatasets.includes(key)" :total="dataset.total" :label="dataset.label" @click.native="toggleDayStatisticsDataset(key)" />
        </template>
      </KeyValues>
    </card-minimal>
  </div>
</template>

<script>

import moment from 'moment';

import { AnalyticsService } from '@/services/analytics.service';
import KeyValues from '@/components/Statistics/KeyValues.vue';
import Key from '@/components/Statistics/Key.vue';

export default {
  components: {
    Key,
    KeyValues,
  },
  computed: {
    /**
     * Return current video
     * @returns {Object}
     */
    video() {
      return this.$store.getters['Video/current'];
    },
    timeera: {
      get() {
        return [
          { value: 'from-7', string: '7 days from published' },
          { value: 'from-30', string: '30 days from published' },
          { value: 'past-7', string: 'Past 7 days' },
          { value: 'past-30', string: 'Past 30 days' },
        ].map(item => ({ ...item, selected: item.value === this.dayStats.timeEra }));
      },
      async set(value) {
        const [type, days] = value.split('-');
        this.dayStats.timeEra = value;
        this.dayStats.type = type;
        this.dayStats.days = parseInt(days, 10);

        await this.getDayStatistics();
        const datasets = this.dayStats.activeDatasets;
        this.dayStats.activeDatasets = [];
        this.dayStats.labels = [];
        this.dayStats.datasets = [];
        datasets.forEach(dataset => this.toggleDayStatisticsDataset(dataset));
      },
    },
  },
  methods: {
    async getDayStatistics() {
      const {
        video: {
          created_at,
        },
        dayStats: {
          type,
          days,
        },
      } = this;

      const from = type === 'past' ? moment().subtract(days - 1, 'days').format('YYYY-MM-DD') : moment(created_at).format('YYYY-MM-DD');
      const to = type === 'past' ? moment().format('YYYY-MM-DD') : moment(created_at).add(days - 1, 'days').format('YYYY-MM-DD');

      const {
        visits_week = [],
        plays_week = [],
      } = await AnalyticsService.range(this.video.id, from, to);

      const data = [
        {
          dataset: visits_week.map(i => ({ ...i, date: moment.unix(i.date).format('YYYY-MM-DDTHH:mm:ssZ') })),
          event: 'pageview',
          state: 'recorded',
          sum: 0,
        },
        {
          dataset: plays_week,
          event: 'playback',
          state: 'recorded',
          sum: 0,
        },
      ];

      this.dayStats.data = data;
      const sortedDatasets = [];

      // Clear all datasets data
      Object.keys(this.dayStats.graph).forEach((dataset) => {
        this.dayStats.graph[dataset].data = [];
        this.dayStats.graph[dataset].total = 0;

        if (typeof this.dayStats.graph[dataset].showIf !== 'undefined' ? this[this.dayStats.graph[dataset].showIf] : true) {
          this.dayStats.graph[dataset].show = true;
        }
      });

      data.forEach((event) => {
        if (typeof this.dayStats.graph[event.event] !== 'undefined' && event.state !== 'upcoming') {
          if (!sortedDatasets.includes(event.event)) {
            sortedDatasets.push(event.event);
          }
          this.dayStats.graph[event.event].data = this.dayStats.graph[event.event].data.concat(event.dataset);
        }
      });

      sortedDatasets.forEach((dataset) => {
        // Clear datasets total
        this.dayStats.graph[dataset].total = 0;
        const datasetData = {};
        let total = 0;
        this.dayStats.graph[dataset].data.forEach((entry) => {
          if (typeof datasetData[entry.date] === 'undefined') {
            datasetData[entry.date] = 0;
          }
          datasetData[entry.date] += entry.value;
          total += parseInt(entry.value, 10);
        });

        this.dayStats.graph[dataset].total = total;
        this.dayStats.graph[dataset].data = Object.keys(datasetData).map(key => ({ date: key, value: datasetData[key] }));
      });
    },
    /**
     * Toggle dayStatistics datasets
     * @returns {Void}
     */
    toggleDayStatisticsDataset(dataset) {
      const {
        video: {
          created_at,
        },
        dayStats: {
          type,
        },
      } = this;

      let {
        dayStats: {
          days,
        },
      } = this;

      const index = this.dayStats.datasets.findIndex(d => d.label === this.dayStats.graph[dataset].label);
      if (index >= 0) {
        if (this.dayStats.datasets.length === 1) {
          return;
        }

        this.dayStats.datasets.splice(index, 1);
        this.dayStats.activeDatasets.splice(this.dayStats.activeDatasets.indexOf(dataset), 1);
        return;
      }

      const date = type === 'past' ? moment() : moment(created_at);
      const labels = [date.format('DD/M')];
      let n = days - 1;
      while (n !== 0) {
        if (type === 'past') {
          date.subtract(1, 'day');
          if (date.isBefore(moment(created_at))) {
            days -= n;
            break;
          }
        } else {
          date.add(1, 'day');
          if (date.isAfter(moment())) {
            days -= n;
            break;
          }
        }
        labels.push(date.format('DD/M'));
        n -= 1;
      }

      this.dayStats.labels = type === 'past' ? labels.reverse() : labels;

      const data = new Array(days).fill(0);
      this.dayStats.graph[dataset].data.forEach((entry) => {
        const diff = type === 'past' ? Math.floor(moment().diff(new Date(entry.date), 'hours') / 24) : Math.abs(Math.floor(moment(created_at).diff(new Date(entry.date), 'hours') / 24));
        data[diff] += entry.value;
      });

      this.dayStats.datasets.push({
        backgroundColor: this.dayStats.graph[dataset].color,
        borderColor: this.dayStats.graph[dataset].color,
        label: this.dayStats.graph[dataset].label,
        data: type === 'past' ? data.reverse() : data,
        tension: 0.2,
        beginAtZero: true,
        pointRadius: 0,
        pointHitRadius: 20,
      });

      this.dayStats.activeDatasets.push(dataset);
    },
  },
  data() {
    return {
      state: 'loading',
      dayStats: {
        data: null,
        type: 'past',
        days: 7,
        timeEra: 'past-7',
        options: {
          scales: {
            y: {
              ticks: {
                display: true,
              },
            },
          },
        },
        graph: {
          pageview: {
            color: '#007FFF',
            colorName: 'blue',
            icon: 'eye-24',
            label: 'Viewers',
            show: true,
            total: 0,
            data: [],
          },
          playback: {
            color: '#00B5D6',
            colorName: 'ocean',
            icon: 'shoppingbag-24',
            label: 'Playback',
            show: false,
            total: 0,
            data: [],
          },
        },
        labels: [],
        datasets: [],
        activeDatasets: [],
      },
    };
  },
  async mounted() {
    await this.getDayStatistics();
    this.toggleDayStatisticsDataset('pageview');
    this.state = 'loaded';
  },
};
</script>
